import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Wrench, Receipt, Car, Phone, MapPin } from "@phosphor-icons/react";
import { API, money, fmtDateTime } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import LangSwitcher from "@/components/LangSwitcher";

const STATUS_ORDER = ["open", "in_progress", "awaiting_approval", "awaiting_parts", "completed", "delivered"];
const STEPS = [
  { key: "open", label: "Checked in" },
  { key: "in_progress", label: "Work in progress" },
  { key: "awaiting_approval", label: "Estimate approval" },
  { key: "completed", label: "Ready" },
  { key: "delivered", label: "Delivered" },
];

export default function PublicJobStatus() {
  const { token } = useParams();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/job-cards/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.status === 404 ? "Not found" : "Something went wrong"));
  }, [token]);

  if (err) {
    return (
      <div className="min-h-screen bg-honeydew text-deep-space grid place-items-center p-6">
        <div className="text-center">
          <div className="text-3xl font-display font-bold mb-2">Link no longer active</div>
          <div className="text-steel text-sm">Please contact your garage for a fresh status link.</div>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen grid place-items-center bg-honeydew text-steel">Loading…</div>;

  const currentStatusIdx = STATUS_ORDER.indexOf(data.status);

  return (
    <div className="min-h-screen bg-honeydew text-deep-space" data-testid="portal-root">
      <div className="border-b border-frosted/60 bg-white/90 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/fender-icon.png" alt="Fender" className="w-9 h-9 shrink-0" width="256" height="190" />
            <div>
              <div className="font-display font-bold leading-tight">{data.garage?.name || "Fender"}</div>
              <div className="text-[10px] uppercase tracking-widest text-steel font-mono">via Fender by SparesHub</div>
            </div>
          </div>
          <LangSwitcher />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10 space-y-6">
        <div>
          <div className="text-xs uppercase font-mono tracking-widest text-strawberry mb-2">{t("portal_title")}</div>
          <h1 className="text-4xl font-display font-bold" data-testid="portal-jc">{data.job_number}</h1>
          <p className="text-steel/80 mt-2">{t("portal_thanks")}</p>
        </div>

        {/* Progress steps */}
        <div className="p-5 bg-white border border-frosted/50">
          <div className="space-y-3">
            {STEPS.map((s, i) => {
              const idx = STATUS_ORDER.indexOf(s.key);
              const done = currentStatusIdx >= idx && currentStatusIdx >= 0;
              const active = data.status === s.key;
              return (
                <div key={s.key} className={`flex items-center gap-3 ${done ? "text-deep-space" : "text-steel"}`}>
                  <div className={`w-6 h-6 grid place-items-center border ${
                    active ? "bg-strawberry border-strawberry text-white" :
                    done ? "bg-emerald-600 border-emerald-600 text-white" : "border-frosted/60"
                  }`}>
                    {done && !active ? <CheckCircle size={14} weight="fill" /> : <span className="font-mono text-xs">{i + 1}</span>}
                  </div>
                  <div className="text-sm font-mono uppercase tracking-widest">{s.label}</div>
                  {active && <span className="ml-2 text-xs font-mono text-strawberry status-pulse">CURRENT</span>}
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div className="p-5 bg-white border border-frosted/50">
            <div className="text-[10px] uppercase font-mono tracking-widest text-steel mb-3">Vehicle</div>
            <div className="flex items-center gap-2">
              <Car size={20} className="text-strawberry" weight="duotone" />
              <div>
                <div className="font-display text-lg">{data.vehicle?.make} {data.vehicle?.model}</div>
                <div className="font-mono text-strawberry">{data.vehicle?.reg_number}</div>
              </div>
            </div>
          </div>
          <div className="p-5 bg-white border border-frosted/50">
            <div className="text-[10px] uppercase font-mono tracking-widest text-steel mb-3">Garage contact</div>
            <div className="text-sm">{data.garage?.name}</div>
            {data.garage?.phone && (
              <a href={`tel:${data.garage.phone.replace(/[^0-9+]/g,"")}`} data-testid="portal-call-btn" className="mt-2 inline-flex items-center gap-2 text-strawberry hover:text-strawberry font-mono">
                <Phone size={14} weight="fill" /> {data.garage.phone}
              </a>
            )}
            {data.garage?.address && (
              <div className="mt-1 flex items-start gap-2 text-xs text-steel">
                <MapPin size={12} className="mt-0.5" /> {data.garage.address}
              </div>
            )}
          </div>
        </div>

        {(data.tasks || []).length > 0 && (
          <div className="p-5 bg-white border border-frosted/50">
            <div className="flex items-center gap-2 mb-3">
              <Wrench size={18} className="text-strawberry" weight="duotone" />
              <div className="font-display font-semibold">{t("portal_tasks")}</div>
            </div>
            <div className="space-y-2">
              {data.tasks.map((t2, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span>{t2.task_name}</span>
                  <span className={`text-[10px] uppercase font-mono tracking-widest px-2 py-1 border ${
                    t2.status === "done" ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/30" :
                    t2.status === "in_progress" ? "bg-strawberry/15 text-strawberry border-strawberry/30" :
                    "bg-zinc-500/15 text-steel border-zinc-500/30"
                  }`}>{t2.status.replace("_"," ")}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.estimate && (
          <div className="p-5 bg-white border border-frosted/50">
            <div className="font-display font-semibold mb-2">{t("portal_estimate")} v{data.estimate.version}</div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Status: {data.estimate.status}</span>
              <span className="font-mono text-2xl text-strawberry">₹ {money(data.estimate.total)}</span>
            </div>
          </div>
        )}

        {data.invoice && (
          <div className="p-5 bg-white border border-frosted/50">
            <div className="flex items-center gap-2 mb-2">
              <Receipt size={18} className="text-strawberry" weight="duotone" />
              <div className="font-display font-semibold">{t("portal_invoice")} · {data.invoice.invoice_number}</div>
            </div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-[10px] uppercase font-mono tracking-widest text-steel">Total</div>
                <div className="font-mono text-lg">₹ {money(data.invoice.total)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-mono tracking-widest text-steel">Paid</div>
                <div className="font-mono text-lg text-emerald-500">₹ {money(data.invoice.paid_amount)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-mono tracking-widest text-steel">Balance</div>
                <div className="font-mono text-lg text-red-400">₹ {money(data.invoice.balance)}</div>
              </div>
            </div>
          </div>
        )}

        <div className="text-xs text-deep-space font-mono text-center pt-6">
          Updated {fmtDateTime(data.updated_at || data.created_at)} · Powered by Fender by SparesHub
        </div>
      </div>
    </div>
  );
}
